import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  PhoneCall,
  ExternalLink,
  LifeBuoy,
  Sparkles,
  ShieldCheck,
  BookOpen,
  Gauge,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const { user } = useAuth();
  const externalUrl = process.env.REACT_APP_EXTERNAL_APP_URL || "https://app.help4emergency.de";
  const displayName = user?.name || user?.email?.split("@")[0] || "Early Tester";

  return (
    <div className="bg-[#FCFCFB]" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 lg:py-16">
        {/* Alpha notice */}
        <div
          data-testid="alpha-notice"
          className="rounded-2xl border border-[#FCEBD6] bg-[#FFF7EA] p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between"
        >
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-[#FCEBD6] grid place-items-center text-[#B36B15]">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm font-semibold text-[#B36B15] uppercase tracking-[0.14em]">
                Alpha-Stand · Early Tester
              </div>
              <p className="mt-1 text-sm text-[#5A5B5C] max-w-2xl leading-relaxed">
                Vielen Dank, dass Sie Help4Emergency in der Alpha begleiten. Funktionen können sich
                ändern, neue Szenarien kommen laufend hinzu. Bitte melden Sie Probleme über die
                Support-Seite — jedes Feedback zählt.
              </p>
            </div>
          </div>
          <Link to="/support" data-testid="dashboard-support-btn">
            <Button variant="outline" className="bg-white border-[#EAEBE6] hover:bg-[#F4F5F2] text-[#272525] rounded-lg">
              <LifeBuoy className="h-4 w-4 mr-1.5" /> Fehler melden
            </Button>
          </Link>
        </div>

        {/* Hero */}
        <div className="mt-10 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <div className="text-sm uppercase tracking-[0.18em] font-semibold text-[#A7B584]">
              Willkommen
            </div>
            <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#5A5B5C] tracking-tight leading-tight">
              Bereit für den nächsten Notruf, {displayName}?
            </h1>
            <p className="mt-4 text-lg text-[#272525] leading-relaxed max-w-xl">
              Starten Sie die KI-Leitstelle und üben Sie realistische 112-Notrufe per Sprache.
              Nach jeder Simulation erhalten Sie einen Score 0–100 und drei konkrete Feedback-Sätze.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a href={externalUrl} target="_blank" rel="noopener noreferrer" data-testid="open-app-btn">
                <Button className="w-full sm:w-auto bg-[#A7B584] hover:bg-[#8C9A69] text-white rounded-lg px-6 py-6 text-base font-semibold">
                  <PhoneCall className="h-4 w-4 mr-2" /> App öffnen
                  <ExternalLink className="h-4 w-4 ml-2 opacity-80" />
                </Button>
              </a>
              <Link to="/support" data-testid="dashboard-feedback-btn">
                <Button
                  variant="outline"
                  className="w-full sm:w-auto bg-white border-[#EAEBE6] hover:bg-[#F4F5F2] text-[#272525] rounded-lg px-6 py-6 text-base font-semibold"
                >
                  Feedback geben
                </Button>
              </Link>
            </div>

            <p className="mt-4 text-xs text-[#5A5B5C]">
              Hinweis: Die App öffnet in einem neuen Tab. Ihre Early-Tester-Sitzung bleibt hier aktiv.
            </p>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-white border border-[#EAEBE6] rounded-2xl p-6 shadow-sm">
              <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[#A7B584]">
                Ihr Konto
              </div>
              <div className="mt-4 space-y-3">
                <Row label="E-Mail" value={user?.email || "—"} testid="account-email" />
                <Row label="Name" value={user?.name || "—"} testid="account-name" />
                <Row label="Rolle" value={user?.role || "user"} testid="account-role" />
                <Row
                  label="Login-Art"
                  value={user?.provider === "google" ? "Google" : "E-Mail / Passwort"}
                  testid="account-provider"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Tips grid */}
        <div className="mt-14 grid md:grid-cols-3 gap-5">
          {[
            {
              icon: BookOpen,
              t: "Stimme auswählen",
              d: "Wählen Sie Bella oder Daniel — beide führen ruhig durch die 5 W-Fragen.",
            },
            {
              icon: Gauge,
              t: "Score verstehen",
              d: "Bewertung von 0–100 plus 3 ehrliche Feedback-Sätze — keine Schönfärberei.",
            },
            {
              icon: ShieldCheck,
              t: "Sicher üben",
              d: "Klar markierter Trainingsmodus. Keine echten Einsatzkräfte werden alarmiert.",
            },
          ].map(({ icon: Icon, t, d }) => (
            <div key={t} className="bg-white border border-[#EAEBE6] rounded-xl p-6" data-testid={`tip-${t}`}>
              <div className="h-10 w-10 rounded-lg bg-[#A7B584]/15 grid place-items-center text-[#8C9A69]">
                <Icon className="h-5 w-5" />
              </div>
              <div className="mt-4 font-semibold text-[#5A5B5C]">{t}</div>
              <p className="mt-1.5 text-sm text-[#272525] leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2 border-b border-[#EAEBE6] last:border-0" data-testid={testid}>
      <span className="text-xs uppercase tracking-[0.14em] text-[#5A5B5C] font-semibold">{label}</span>
      <span className="text-sm text-[#272525] truncate max-w-[60%]">{value}</span>
    </div>
  );
}
